import { useState } from "react";
import { PlusCircleIcon, UsersIcon } from "@heroicons/react/24/outline";
import { Button, Container, EmptyState, PageHeader, RowAction, Table, Tag } from "../../ui";
import { countryName, formatKigaliDate, useSponsors } from "../model";
import NewSponsorDialog from "./NewSponsorDialog";
import SponsorMembersDialog from "./SponsorMembersDialog";
import { canManageScholarships } from "../access";

// "Sponsors": the organisations that fund scholarships, and the staff who act for them as partners
export default function SponsorsPage() {
  const { data: sponsors = [], isPending, isError, refetch } = useSponsors();
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Looked up by ID, so the dialog shows the latest member count after a change
  const open = sponsors.find((s) => s.id === openId) ?? null;

  const columns = [
    { key: "name", label: "Partner", render: (s) => <span className="font-medium text-slate-800">{s.name}</span>, accessor: (s) => s.name },
    { key: "country", label: "Country", accessor: (s) => countryName(s.countryCode), filter: "Any country" },
    { key: "memberCount", label: "Members", accessor: (s) => s.memberCount, sortValue: (s) => s.memberCount },
    { key: "createdAt", label: "Added", accessor: (s) => formatKigaliDate(s.createdAt), sortValue: (s) => s.createdAt },
    { key: "active", label: "Status", accessor: (s) => (s.active ? "Active" : "Inactive"), render: (s) => <Tag color={s.active ? "green" : "gray"}>{s.active ? "Active" : "Inactive"}</Tag> },
    { key: "members", label: "", exportable: false, render: (s) => <RowAction Icon={UsersIcon} label="Members" onClick={() => setOpenId(s.id)} /> },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Partners"
        actions={
          canManageScholarships() && (
            <Button icon={<PlusCircleIcon className="h-5 w-5" />} onClick={() => setCreating(true)}>
              New partner
            </Button>
          )
        }
      />
      {isError ? (
        <EmptyState title="Could not load the partners" onRefresh={refetch} />
      ) : (
        <Table cardView
          columns={columns}
          data={sponsors}
          onRowClick={(s) => setOpenId(s.id)}
          isLoading={isPending}
          searchable
          exportName="sponsors"
          emptyTitle="No partners yet"
        />
      )}
      <NewSponsorDialog open={creating} onClose={() => setCreating(false)} onCreated={(s) => setOpenId(s.id)} />
      <SponsorMembersDialog key={openId} sponsor={open} onClose={() => setOpenId(null)} />
    </Container>
  );
}
